import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import Errors from '../common/Errors';
import Select from '../common/Select';
import { DividedRow, Half, Third, Full } from '../common/RowLengths';
import {
  fieldTypes,
  newWidthOptions,
  validatorOptions,
  newFieldDefaults,
  checkCharLimit,
  checkCharMin,
  noZeros,
} from './constants/editField';
import * as f from './constants/subtypes';
import { handleCreateField } from './constants/handleField';
import { sortable } from '../../helpers/sorters';
import Accordion from '../common/Accordion';
import Icon from '../common/Icon';

// TODO:
// Filetype options?

const NewField = ({
  setFields,
  sortValue,
  fsID,
  informantID,
  setInformantID,
  setFieldValidity,
}) => {
  const [isCreating, setIsCreating] = useState(sortValue === 0);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    setError,
    formState: { errors, isSubmitSuccessful },
  } = useForm({
    defaultValues: newFieldDefaults,
    criteriaMode: 'all',
    shouldUnregister: true,
  });

  useEffect(() => {
    if (isSubmitSuccessful) reset();
  }, [isSubmitSuccessful]);

  // these values are updated at render, not here...
  let type = watch('type');
  let validator = watch('validator');
  let quickEdit = watch('quick_edit');
  let informsGlobal = watch('informs_global');

  const toggleCreating = (e) => {
    e.preventDefault();
    if (isCreating) {
      reset();
      document.activeElement.blur();
    }
    setIsCreating(!isCreating);
  };

  function makeGlobalInformerRequired() {
    if (watch('informs_global') === true) {
      setValue('required', true, { shouldDirty: true });
    }
  }

  function watchQuickEdit() {
    if (watch('quick_edit') === false) {
      setValue('universal_quick_edit', false, { shouldDirty: true });
      setValue('unread_quick_edit', false, { shouldDirty: true });
    }
  }

  function watchValidator() {
    if (!f.validatorLimits(watch('validator'))) {
      setValue('char_min', null, { shouldDirty: true });
      setValue('char_limit', null, { shouldDirty: true });
    }
  }

  const onSubmit = async (formData) => {
    let field = { ...formData, sort: sortValue, fieldset_id: fsID };
    if (f.isOptionType(field.type) && field.validator != 'date') {
      // date is the only allowed validator for option types (for filter sorting purposes)
      field.validator = null;
    }
    let newField = await handleCreateField(field);
    if (newField.ok) {
      setFields((fields) => sortable([...fields, newField.data]));

      // new Field will not have any valid responses, so manually set to false
      setFieldValidity((validity) => [...validity, [newField.data.id, false]]);

      if (newField.data.informs_global) setInformantID(newField.data.id);
    } else if (newField.errors) {
      Object.entries(newField.errors).forEach(([key, message]) =>
        setError(key, { types: { [key]: message } })
      );
    }
  };

  return (
    <Accordion
      id='new-field-dropdown'
      expanded={isCreating}
      toggle={toggleCreating}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset>
          <DividedRow>
            <Third labelFor='req' title='Required:' hasCheckbox>
              <input
                id='req'
                type='checkbox'
                {...register('required')}
                disabled={informsGlobal}
              />
            </Third>
          </DividedRow>

          <DividedRow>
            <Full title='Display Name:'>
              <input
                placeholder='Displayed to user'
                {...register('display_name')}
              />
            </Full>
          </DividedRow>

          <DividedRow>
            <Half title='Name:'>
              <input
                placeholder='Used for management only'
                {...register('name', { required: 'Required' })}
              />
              <ErrorMessage
                errors={errors}
                name='name'
                render={({ messages }) => <Errors messages={messages} />}
              />
            </Half>
            <Half title='Help Text:'>
              <input
                placeholder='Field help text...'
                {...register('help_text')}
              />
            </Half>
          </DividedRow>

          <DividedRow>
            <Half title='Type:'>
              <Select
                content={fieldTypes}
                register={{ ...register('type', { required: 'Required' }) }}
              />
              <ErrorMessage
                errors={errors}
                name='type'
                render={({ messages }) => <Errors messages={messages} />}
              />
            </Half>

            <Half title={type != 'file' ? 'Width:' : ''}>
              {type != 'file' ? (
                <>
                  <Select
                    content={newWidthOptions}
                    register={{
                      ...register('width', {
                        required: 'Required',
                      }),
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name='width'
                    render={({ messages }) => <Errors messages={messages} />}
                  />
                </>
              ) : (
                <Full title='Allowed Filetypes:'>
                  <input {...register('file_ext_whitelist')} />
                </Full>
              )}
            </Half>
          </DividedRow>

          {f.isTextType(type) && (
            <DividedRow>
              <Third title='Validator:'>
                <Select
                  content={validatorOptions}
                  register={{
                    ...register('validator', {
                      onChange: () => watchValidator(),
                    }),
                  }}
                />
              </Third>

              <div
                className={
                  f.validatorLimits(validator) ? '' : 'disabled-fields'
                }
              >
                <Third title='Character minimum:'>
                  <input
                    type='number'
                    {...register('char_min', {
                      validate: {
                        min: (v) => checkCharMin(v, getValues('char_limit')),
                        minZero: (v) => noZeros(v),
                      },
                      setValueAs: (v) => (v == '' ? null : v),
                      deps: 'char_limit',
                    })}
                    placeholder='e.g. 2'
                    disabled={!f.validatorLimits(validator)}
                  />
                  <ErrorMessage
                    errors={errors}
                    name='char_min'
                    render={({ messages }) => <Errors messages={messages} />}
                  />
                </Third>
                <Third title='Character limit:'>
                  <input
                    type='number'
                    {...register('char_limit', {
                      validate: {
                        limit: (v) => checkCharLimit(v, getValues('char_min')),
                        limitZero: (v) => noZeros(v),
                      },
                      setValueAs: (v) => (v == '' ? null : v),
                      deps: 'char_min',
                    })}
                    placeholder='e.g. 7'
                    disabled={!f.validatorLimits(validator)}
                  />
                  <ErrorMessage
                    errors={errors}
                    name='char_limit'
                    render={({ messages }) => <Errors messages={messages} />}
                  />
                </Third>
              </div>
            </DividedRow>
          )}

          <DividedRow>
            {f.isPlaceholderType(type) && (
              <Half title='Placeholder:'>
                <input placeholder='e.g. Name' {...register('placeholder')} />
              </Half>
            )}
            {f.isOptionType(type) && (
              <Half title='Validator:'>
                <Select
                  content={validatorOptions.filter((v) =>
                    ['none', 'date'].includes(v.label)
                  )}
                  register={{
                    ...register('validator', {
                      onChange: () => watchValidator(),
                    }),
                  }}
                />
              </Half>
            )}

            {f.isTextType(type) && (
              <Half title='Default Value:'>
                <input
                  placeholder='e.g. Texas'
                  {...register('default_value')}
                />
              </Half>
            )}
            <Full>
              {f.isOptionType(type) && (
                <div className='third pl-0 mt-1'>
                  <input id='alpha' type='checkbox' {...register('alpha')} />
                  <label htmlFor='alpha'>Alphabetical Sort:</label>
                </div>
              )}

              {f.allowQuickEdit(type) && (
                <>
                  <div className='third pl-0 mt-1'>
                    <input
                      id='quick-edit'
                      type='checkbox'
                      {...register('quick_edit', {
                        onChange: () => watchQuickEdit(),
                      })}
                    />
                    <label htmlFor='quick-edit'>Allow Quick Edit?</label>
                  </div>

                  <div className='quick-dependent'>
                    <div className='third pl-0 mt-1'>
                      <input
                        id='universal-qe'
                        type='checkbox'
                        {...register('universal_quick_edit')}
                        disabled={!quickEdit}
                      />
                      <label
                        htmlFor='universal-qe'
                        className={!quickEdit ? 'disabled' : ''}
                      >
                        Quick Edit for All Roles?
                      </label>
                    </div>

                    <div className='third pl-0 mt-1'>
                      <input
                        id='unread-qe'
                        type='checkbox'
                        {...register('unread_quick_edit')}
                        disabled={!quickEdit}
                      />
                      <label
                        htmlFor='unread-qe'
                        className={!quickEdit ? 'disabled' : ''}
                      >
                        Quick Edit for Unread Submissions?
                      </label>
                    </div>
                  </div>
                </>
              )}

              {f.isAllowedQuota(type) && (
                <div className='third pl-0 mt-1'>
                  {informantID ? (
                    <label>Informs Global: Unavailable</label>
                  ) : (
                    <>
                      <input
                        id='informs-global'
                        type='checkbox'
                        {...register('informs_global', {
                          onChange: () => makeGlobalInformerRequired(),
                        })}
                      />
                      <label htmlFor='informs-global'>Informs Global:</label>
                    </>
                  )}
                </div>
              )}
            </Full>
          </DividedRow>
        </fieldset>

        <div className='controls'>
          <button className='single' type='submit'>
            Save
            <Icon icon='save' />
          </button>
          <button onClick={toggleCreating} className='blue'>
            Cancel
            <Icon icon='xmark' />
          </button>
        </div>
      </form>
    </Accordion>
  );
};

export default NewField;
